const DEFAULT_EVENT_TIME_ZONE = "Asia/Kolkata";

type FormatterKind =
  | "booking-window"
  | "date-key"
  | "date-label"
  | "hour"
  | "time";

const FORMATTERS = new Map<string, Intl.DateTimeFormat>();

/**
 * Occurrence timestamps are instants, but their calendar date and display time
 * belong to the event's timezone. Falling back here also keeps old occurrence
 * rows (created before timezone was required) deterministic during SSR.
 */
export function resolveEventTimeZone(timeZone: string | null | undefined) {
  const candidate = timeZone?.trim() || DEFAULT_EVENT_TIME_ZONE;
  try {
    new Intl.DateTimeFormat("en-US", { timeZone: candidate }).format(0);
    return candidate;
  } catch {
    return DEFAULT_EVENT_TIME_ZONE;
  }
}

function getFormatter(kind: FormatterKind, timeZone: string) {
  const resolvedTimeZone = resolveEventTimeZone(timeZone);
  const cacheKey = `${kind}:${resolvedTimeZone}`;
  const cached = FORMATTERS.get(cacheKey);
  if (cached) return cached;

  let formatter: Intl.DateTimeFormat;
  switch (kind) {
    case "date-key":
      formatter = new Intl.DateTimeFormat("en-CA-u-ca-gregory-nu-latn", {
        timeZone: resolvedTimeZone,
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
      });
      break;
    case "date-label":
      formatter = new Intl.DateTimeFormat("en-IN", {
        timeZone: resolvedTimeZone,
        weekday: "short",
        day: "numeric",
        month: "short",
      });
      break;
    case "hour":
      formatter = new Intl.DateTimeFormat("en-US", {
        timeZone: resolvedTimeZone,
        hour: "numeric",
        hour12: true,
      });
      break;
    case "time":
      formatter = new Intl.DateTimeFormat("en-US", {
        timeZone: resolvedTimeZone,
        hour: "numeric",
        minute: "2-digit",
        hour12: true,
      });
      break;
    case "booking-window":
      formatter = new Intl.DateTimeFormat("en-IN", {
        timeZone: resolvedTimeZone,
        weekday: "short",
        month: "short",
        day: "2-digit",
        hour: "numeric",
        minute: "2-digit",
      });
      break;
  }

  FORMATTERS.set(cacheKey, formatter);
  return formatter;
}

export function toEventDateKey(date: Date, timeZone: string) {
  const parts = getFormatter("date-key", timeZone).formatToParts(date);
  const value = (type: Intl.DateTimeFormatPartTypes) =>
    parts.find((part) => part.type === type)?.value ?? "";
  return `${value("year")}-${value("month")}-${value("day")}`;
}

export function isSameEventDay(left: Date, right: Date, timeZone: string) {
  return toEventDateKey(left, timeZone) === toEventDateKey(right, timeZone);
}

export function formatEventDate(date: Date, timeZone: string) {
  return getFormatter("date-label", timeZone).format(date);
}

export function formatEventTime(date: Date, timeZone: string) {
  return getFormatter("time", timeZone).format(date);
}

export function formatEventTimeRange(start: Date, end: Date, timeZone: string) {
  return `${formatEventTime(start, timeZone)} - ${formatEventTime(end, timeZone)}`;
}

export function formatEventSlotStart(date: Date, timeZone: string) {
  const timeFormatter = getFormatter("time", timeZone);
  const minute = timeFormatter
    .formatToParts(date)
    .find((part) => part.type === "minute")?.value;
  return getFormatter(minute === "00" ? "hour" : "time", timeZone).format(date);
}

export function formatEventBookingWindow(date: Date, timeZone: string) {
  return getFormatter("booking-window", timeZone).format(date);
}
